
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SolutionsPage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsHeaderVisible(false);
        setIsMobileMenuOpen(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 bg-white shadow-sm z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold text-black select-none cursor-pointer" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link>
              <Link to="/services" className="hover:text-blue-400 transition-colors cursor-pointer">Services</Link>
              <Link to="/solutions" className="text-blue-400 cursor-pointer">Solutions</Link>
              <Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About</Link>
              <Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button
                className="text-gray-600 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 mt-4 py-4">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Home
              </Link>
              <Link
                to="/services"
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Services
              </Link>
              <Link
                to="/solutions"
                className="text-blue-400 px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Solutions
              </Link>
              <Link
                to="/about"
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                About
              </Link>
              <Link
                to="/contact"
                className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                onClick={handleMobileNavClick}
              >
                Contact
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white pt-20">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/9e9419430f9822ae0b1dcfe9a806924a.jpg')`
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="block">Automation</span>
            <span className="block text-blue-400">Solutions</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-300">
            Discover our comprehensive range of warehouse automation solutions designed to optimize every aspect of your operations
          </p>
        </div>
      </section>

      {/* Featured Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Featured Automation Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-leading automation technologies that transform warehouse operations and drive efficiency
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Advanced%20robotic%20picking%20system%20with%20articulated%20robot%20arms%20handling%20packages%20in%20a%20modern%20warehouse%20with%20clean%20industrial%20design%20and%20blue%20lighting%20accents&width=600&height=400&seq=robotic-picking-001&orientation=landscape"
                alt="Robotic Picking System"
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Robotic Picking Systems</h3>
              <p className="text-gray-600 mb-6">
                Advanced robotic arms with AI-powered vision systems for precise picking, packing, and sorting operations. Increase accuracy by 99.5% while reducing labor costs.
              </p>
              <ul className="space-y-2">
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  AI-powered vision recognition
                </li>
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Multi-SKU handling capability
                </li>
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Real-time performance analytics
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Autonomous%20mobile%20robots%20AGVs%20navigating%20through%20warehouse%20aisles%20carrying%20pallets%20and%20packages%20in%20a%20modern%20automated%20facility%20with%20clean%20industrial%20design&width=600&height=400&seq=agv-systems-001&orientation=landscape"
                alt="AGV Systems"
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AGV & AMR Systems</h3>
              <p className="text-gray-600 mb-6">
                Autonomous guided vehicles and mobile robots for seamless material transport, inventory management, and workflow optimization throughout your facility.
              </p>
              <ul className="space-y-2">
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Autonomous navigation technology
                </li>
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Fleet management system
                </li>
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Collision avoidance sensors
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Advanced%20automated%20packing%20machine%20with%20robotic%20arms%20packaging%20products%20on%20conveyor%20belt%20in%20modern%20warehouse%20facility%20with%20blue%20lighting%20and%20industrial%20design&width=600&height=400&seq=packing-machine-001&orientation=landscape"
                alt="Automated Packing Machine"
                className="w-full h-64 object-cover rounded-lg mb-6"
              />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Automated Packing Machines</h3>
              <p className="text-gray-600 mb-6">
                High-speed automated packing systems that handle multiple product types with precision packaging, labeling, and quality control for maximum efficiency.
              </p>
              <ul className="space-y-2">
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Multi-format packaging capability
                </li>
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Integrated labeling system
                </li>
                <li className="text-gray-700 flex items-center">
                  <i className="ri-check-line text-blue-600 mr-2"></i>
                  Quality control sensors
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-truck-line',
                title: 'Conveyor Systems',
                description: 'High-speed automated conveyor networks for efficient material flow and sorting operations'
              },
              {
                icon: 'ri-stack-line',
                title: 'AS/RS Systems',
                description: 'Automated storage and retrieval systems for maximum space utilization and inventory control'
              },
              {
                icon: 'ri-qr-code-line',
                title: 'WMS Integration',
                description: 'Seamless warehouse management system integration for real-time visibility and control'
              },
              {
                icon: 'ri-voice-recognition-line',
                title: 'Voice Picking',
                description: 'Hands-free voice-directed picking systems for improved accuracy and productivity'
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Pick-to-Light',
                description: 'LED-guided picking systems that reduce errors and increase picking speed'
              },
              {
                icon: 'ri-bar-chart-line',
                title: 'Analytics Platform',
                description: 'Advanced analytics and reporting tools for performance optimization and insights'
              }
            ].map((solution, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${solution.icon} text-xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                <p className="text-gray-600">{solution.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industry-Specific Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored automation solutions designed for the unique requirements of different industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {[
              {
                industry: 'E-Commerce & Retail',
                image: 'https://readdy.ai/api/search-image?query=E-commerce%20warehouse%20automation%20with%20robotic%20systems%20handling%20packages%20and%20orders%20in%20a%20modern%20fulfillment%20center%20with%20clean%20industrial%20design%20and%20efficient%20workflow&width=600&height=400&seq=ecommerce-solution-001&orientation=landscape',
                solutions: [
                  'High-speed order fulfillment systems',
                  'Automated picking and packing',
                  'Returns processing automation',
                  'Multi-channel inventory management'
                ],
                benefits: '300% faster order processing, 99.8% accuracy rate'
              },
              {
                industry: 'Manufacturing',
                image: 'https://readdy.ai/api/search-image?query=Manufacturing%20warehouse%20automation%20with%20robotic%20systems%20handling%20raw%20materials%20and%20finished%20goods%20in%20an%20industrial%20facility%20with%20clean%20modern%20design&width=600&height=400&seq=manufacturing-solution-001&orientation=landscape',
                solutions: [
                  'Raw material handling automation',
                  'Production line integration',
                  'Quality control systems',
                  'Just-in-time inventory management'
                ],
                benefits: '40% reduction in material handling costs'
              },
              {
                industry: 'Food & Beverage',
                image: 'https://readdy.ai/api/search-image?query=Food%20and%20beverage%20warehouse%20automation%20with%20temperature%20controlled%20storage%20and%20robotic%20handling%20systems%20in%20a%20clean%20hygienic%20industrial%20facility&width=600&height=400&seq=food-beverage-solution-001&orientation=landscape',
                solutions: [
                  'Temperature-controlled automation',
                  'FIFO inventory rotation',
                  'Food safety compliance systems',
                  'Cold chain management'
                ],
                benefits: '100% traceability, zero contamination risk'
              },
              {
                industry: 'Pharmaceuticals',
                image: 'https://readdy.ai/api/search-image?query=Pharmaceutical%20warehouse%20automation%20with%20secure%20robotic%20systems%20handling%20medical%20supplies%20and%20drugs%20in%20a%20sterile%20clean%20room%20environment%20with%20advanced%20technology&width=600&height=400&seq=pharmaceutical-solution-001&orientation=landscape',
                solutions: [
                  'Secure automated dispensing',
                  'Regulatory compliance tracking',
                  'Temperature monitoring systems',
                  'Batch tracking and serialization'
                ],
                benefits: 'Enhanced security and accuracy'
              }
            ].map((industry, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden">
                <img
                  src={industry.image}
                  alt={`${industry.industry} Solutions`}
                  className="w-full h-64 object-cover"
                />
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{industry.industry}</h3>
                  <ul className="space-y-2 mb-6">
                    {industry.solutions.map((solution, solutionIndex) => (
                      <li key={solutionIndex} className="text-gray-700 flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2"></i>
                        {solution}
                      </li>
                    ))}
                  </ul>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-800 font-semibold">{industry.benefits}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Advanced Technology Stack
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Cutting-edge technologies that power our automation solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                category: 'Artificial Intelligence',
                technologies: ['Machine Learning', 'Computer Vision', 'Predictive Analytics', 'Neural Networks']
              },
              {
                category: 'Robotics',
                technologies: ['Articulated Arms', 'SCARA Robots', 'Delta Robots', 'Collaborative Robots']
              },
              {
                category: 'Sensors & IoT',
                technologies: ['LIDAR Systems', 'RFID Technology', 'Barcode Scanners', 'Weight Sensors']
              },
              {
                category: 'Software',
                technologies: ['WMS Integration', 'ERP Connectivity', 'Cloud Analytics', 'Mobile Apps']
              }
            ].map((tech, index) => (
              <div key={index} className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4 text-blue-400">{tech.category}</h3>
                <ul className="space-y-2">
                  {tech.technologies.map((technology, techIndex) => (
                    <li key={techIndex} className="text-gray-300 flex items-center">
                      <i className="ri-arrow-right-line text-blue-400 mr-2"></i>
                      {technology}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Find Your Perfect Solution
          </h2>
          <p className="text-xl mb-8">
            Let our experts help you identify the ideal automation solution for your specific requirements
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4 select-none" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Transforming warehouse operations with cutting-edge robotics, AI-powered systems, and intelligent automation solutions. We empower businesses to achieve unprecedented levels of efficiency, accuracy, and cost savings through innovative technology.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Novatronis Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SolutionsPage;
